import type { ReactNode } from 'react';
import styles from './style.module.scss';

/**
 * Props for TwoColumnLayout
 */
export interface TwoColumnLayoutProps {
  /** Optional top content (breadcrumbs, nav) */
  topContent?: ReactNode;
  /** Make top content sticky */
  stickyTop?: boolean;
  /** Optional full-width content above the columns */
  fullWidthContent?: ReactNode;
  /** Main content (2/3 width) */
  mainContent: ReactNode;
  /** Sidebar content (1/3 width) */
  sidebar: ReactNode;
  /** Make sidebar sticky */
  stickySidebar?: boolean;
  /** Make sidebar overlap the full-width content (e.g., gallery) */
  overlapSidebar?: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * TwoColumnLayout - Page Template
 * 
 * Atomic Design: Template
 * Generic two-column layout with optional sections:
 * - Optional top content (breadcrumbs, nav) with opt-in stickiness
 * - Optional full-width section (hero, gallery)
 * - Main content (2/3) + Sidebar (1/3)
 * - Sidebar with opt-in stickiness and overlapping
 * 
 * @example
 * ```tsx
 * <TwoColumnLayout
 *   topContent={<Nav />}
 *   stickyTop
 *   fullWidthContent={<Gallery />}
 *   mainContent={<Article />}
 *   sidebar={<ContactForm />}
 *   stickySidebar
 *   overlapSidebar
 * />
 * ```
 */
export function TwoColumnLayout({
  topContent,
  stickyTop = false,
  fullWidthContent,
  mainContent,
  sidebar,
  stickySidebar = false,
  overlapSidebar = false,
  className,
}: TwoColumnLayoutProps) {
  return (
    <div className={`${styles.container} ${className || ''}`}>
      {topContent && (
        <div className={stickyTop ? styles.topContentSticky : styles.topContent}>
          {topContent}
        </div>
      )}
      {fullWidthContent && (
        <div className={styles.fullWidth}>
          {fullWidthContent}
        </div>
      )}
      <div className={overlapSidebar ? styles.twoColumnRowOverlap : styles.twoColumnRow}>
        <div className={styles.mainContent}>
          {mainContent}
        </div>
        <div className={overlapSidebar ? styles.sidebarOverlap : styles.sidebar}>
          <div className={stickySidebar ? styles.sidebarSticky : undefined}>
            {sidebar}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TwoColumnLayout;
